import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import { Sun, Moon, BookHalf, Funnel, FunnelFill } from "react-bootstrap-icons";
import styled from "styled-components";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LabelList,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip
} from "recharts";
import type { ValueType } from "recharts/types/component/DefaultTooltipContent";

import { getAnalyticsWidget, createExportSchedule, getExportSchedules } from "../../../utils/Requests";
import { getDefaultDateRange, getWidgetTheme, replaceAnalyticsBrandTerminology } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, WidgetTheme, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import { PopPeriodBanner } from "../../../components/analytics";
import { ExportButton, ScheduleExportModal, DocsSidebar, WidgetCard } from "../../../components/analytics";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { ExportContext, ExportableData, ExportSchedule, ExportScheduleRequest } from "../../../services/export";
import InteractiveLineChart from "../../../components/analytics/InteractiveLineChart";
import Logger from "../../../utils/Logger";
import { truncateAxisLabelToWidth } from "../widgets/utils";

type KpiDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type KpiMetricCard = {
    key: string;
    label: string;
    value: number | null;
    changePercent: number | null;
    valueFormat?: "number" | "percent" | "decimal" | "durationHours";
    description?: string;
    invertTrend?: boolean;
};

type SessionTrendPoint = {
    date: string;
    sessionCount: number;
    deviceCount: number;
    playCount: number;
    viewCount: number;
    userCount: number;
    playingUserCount: number;
    viewingUserCount: number;
};

type TopPlatformPoint = {
    devicePlatform: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type TopChannelPoint = {
    thingBrandName: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type TopGenrePoint = {
    genreName: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type KpiWidgetKey =
    | "kpi_traffic"
    | "kpi_user_kpi"
    | "kpi_user_engagement"
    | "kpi_content_engagement"
    | "kpi_traffic_trend"
    | "kpi_top_platforms"
    | "kpi_top_things"
    | "kpi_top_genres";

type WidgetLoadState = {
    isLoading: boolean;
    error: string | null;
};

type WidgetStateMap = Record<KpiWidgetKey, WidgetLoadState>;

const KPI_WIDGET_KEYS: KpiWidgetKey[] = [
    "kpi_traffic",
    "kpi_user_kpi",
    "kpi_user_engagement",
    "kpi_content_engagement",
    "kpi_traffic_trend",
    "kpi_top_platforms",
    "kpi_top_things",
    "kpi_top_genres"
];

const createInitialWidgetStates = (isLoading: boolean): WidgetStateMap => {
    return KPI_WIDGET_KEYS.reduce<WidgetStateMap>((acc, widgetKey) => {
        acc[widgetKey] = { isLoading, error: null };
        return acc;
    }, {} as WidgetStateMap);
};

type KpiDashboardState = {
    isLoading: boolean;
    error: string | null;
    widgetStates: WidgetStateMap;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filterKey: number;
    filtersCollapsed: boolean;
    isDarkTheme: boolean;
    userKpiMetrics: KpiMetricCard[];
    trafficMetrics: KpiMetricCard[];
    userEngagementMetrics: KpiMetricCard[];
    contentEngagementMetrics: KpiMetricCard[];
    sessionTrendData: SessionTrendPoint[];
    topPlatformsData: TopPlatformPoint[];
    showScheduleModal: boolean;
    schedules: ExportSchedule[];
    editingSchedule: ExportSchedule | null;
    TopBrandsData: TopChannelPoint[];
    topGenresData: TopGenrePoint[];
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

const TopMetricSectionsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 16px;
    margin-top: 24px;

    @media (max-width: 1520px) {
        grid-template-columns: 1fr;
    }
`;

const TopMetricSection = styled.div<{ $start: number; $span: number }>`
    grid-column: ${props => `${props.$start} / span ${props.$span}`};

    @media (max-width: 1520px) {
        grid-column: auto;
    }
`;


const SubChartBlock = styled.div<{ theme: WidgetTheme }>`
    margin-top: 18px;

    h6 {
        color: ${props => props.theme.text};
        margin-bottom: 8px;
        text-align: center;
    }
`;

class KpiDashboard extends React.Component<KpiDashboardProps, KpiDashboardState> {
    private containerRef = React.createRef<HTMLDivElement>();
    private static readonly TOP_BRAND_AXIS_WIDTH = 160;
    private static readonly TOP_BRAND_AXIS_TEXT_MAX_WIDTH = 142;
    private static readonly CHART_TOOLTIP_Z_INDEX = 1010;
    private activeRequestId = 0;
    private isMountedFlag = false;

    constructor(props: KpiDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();

        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandName: [],
            devicePlatform: []
        };

        this.state = {
            isLoading: true,
            error: null,
            widgetStates: createInitialWidgetStates(true),
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandNames: [],
                devicePlatforms: []
            },
            filterKey: 0,
            filtersCollapsed: false,
            isDarkTheme: true,
            userKpiMetrics: [],
            trafficMetrics: [],
            userEngagementMetrics: [],
            contentEngagementMetrics: [],
            sessionTrendData: [],
            topPlatformsData: [],
            TopBrandsData: [],
            showScheduleModal: false,
            schedules: [],
            editingSchedule: null,
            topGenresData: [],
            sidebarOpen: false,
            sidebarEntries: []
        };
    }

    componentDidMount(): void {
        this.isMountedFlag = true;
        this.fetchData();
    }

    componentWillUnmount(): void {
        this.isMountedFlag = false;
        this.activeRequestId += 1;
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { userKpiMetrics, trafficMetrics, userEngagementMetrics, contentEngagementMetrics, sessionTrendData, topPlatformsData, TopBrandsData, appliedFilters } = this.state;
        const data: ExportableData[] = [];

        // User KPI metrics
        if (userKpiMetrics.length > 0) {
            data.push({
                title: "User KPI Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "Change %", format: "percent" }
                ],
                rows: userKpiMetrics.map(m => ({ label: m.label, value: m.value, changePercent: m.changePercent })),
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Traffic metrics
        if (trafficMetrics.length > 0) {
            data.push({
                title: "Traffic Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "Change %", format: "percent" }
                ],
                rows: trafficMetrics.map(m => ({ label: m.label, value: m.value, changePercent: m.changePercent })),
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // User engagement metrics
        if (userEngagementMetrics.length > 0) {
            data.push({
                title: "User Engagement Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "Change %", format: "percent" }
                ],
                rows: userEngagementMetrics.map(m => ({ label: m.label, value: m.value, changePercent: m.changePercent })),
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Content engagement metrics
        if (contentEngagementMetrics.length > 0) {
            data.push({
                title: "Content Engagement Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "Change %", format: "percent" }
                ],
                rows: contentEngagementMetrics.map(m => ({ label: m.label, value: m.value, changePercent: m.changePercent })),
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Session trend data
        if (sessionTrendData.length > 0) {
            data.push({
                title: "Session Trend",
                columns: [
                    { key: "date", label: "Date", format: "date" },
                    { key: "sessionCount", label: "Sessions", format: "number" }
                ],
                rows: sessionTrendData,
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Top platforms
        if (topPlatformsData.length > 0) {
            data.push({
                title: "Top Platforms",
                columns: [
                    { key: "devicePlatform", label: "Platform" },
                    { key: "sessionCount", label: "Sessions", format: "number" },
                    { key: "sessionPercentChange", label: "Change %", format: "percent" }
                ],
                rows: topPlatformsData,
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Top brands
        if (TopBrandsData.length > 0) {
            data.push({
                title: "Top Channels",
                columns: [
                    { key: "thingBrandName", label: "Channel" },
                    { key: "sessionCount", label: "Sessions", format: "number" },
                    { key: "sessionPercentChange", label: "Change %", format: "percent" }
                ],
                rows: TopBrandsData,
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "KPI Dashboard",
            pageKey: "kpi_dashboard",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleClick = (): void => {
        this.setState({ showScheduleModal: true, editingSchedule: null });
    };

    private handleScheduleModalHide = (): void => {
        this.setState({ showScheduleModal: false, editingSchedule: null });
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
        const response = await getExportSchedules(this.props.customerConfig);
        this.setState({ schedules: response.schedules });
    };

    private toTitleCase = (value: string): string => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    private mapResponseToMetrics = (
        responseRow: Record<string, number | null | undefined>,
        preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" | "durationHours"; invertTrend?: boolean }>,
        schema?: Record<string, { description?: string }>
    ): KpiMetricCard[] => {
        const preferred = preferredMetricMap
            .filter(metric => Object.prototype.hasOwnProperty.call(responseRow, metric.key))
            .map(metric => ({
                key: metric.key,
                label: metric.label,
                value: responseRow[metric.key] ?? null,
                changePercent: responseRow[metric.changeKey] ?? null,
                valueFormat: metric.valueFormat,
                description: schema?.[metric.key]?.description,
                invertTrend: metric.invertTrend
            }));

        if (preferred.length > 0) {
            return preferred;
        }

        // Fallback: derive metric cards from *_percent_change pairs.
        return Object.keys(responseRow)
            .filter(key => !key.endsWith("_percent_change"))
            .map(key => {
                const changeKey = `${key}_percent_change`;
                return {
                    key,
                    label: this.toTitleCase(key),
                    value: responseRow[key] ?? null,
                    changePercent: responseRow[changeKey] ?? null,
                    valueFormat: key.endsWith("_percent") ? "percent" : "number"
                };
            });
    };

    private mapUserKpiMetrics = (responseRow: Record<string, number>, schema?: Record<string, { description?: string }>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" | "durationHours" }> = [
            { key: "user_count", label: "Users", changeKey: "user_percent_change" },
            { key: "playing_user_count", label: "Playing Users", changeKey: "playing_user_percent_change" },
            { key: "viewing_user_count", label: "Viewing Users", changeKey: "viewing_user_percent_change" },
            { key: "device_count", label: "Devices", changeKey: "device_percent_change" }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap, schema);
    };

    private mapTrafficMetrics = (responseRow: Record<string, number>, schema?: Record<string, { description?: string }>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" | "durationHours" }> = [
            { key: "session_count", label: "Sessions", changeKey: "session_percent_change" },
            { key: "play_count", label: "Video Starts", changeKey: "plays_percent_change" },
            {
                key: "gdpr_status_device_percent",
                label: "GDPR Status Device %",
                changeKey: "gdpr_status_device_percent_change",
                valueFormat: "percent"
            }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap, schema);
    };

    private mapUserEngagementMetrics = (responseRow: Record<string, number>, schema?: Record<string, { description?: string }>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" | "durationHours"; invertTrend?: boolean }> = [
            {
                key: "sessions_per_user",
                label: "Sessions per User",
                changeKey: "sessions_per_user_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "plays_per_user",
                label: "Video Starts per User",
                changeKey: "plays_per_user_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "plays_per_session",
                label: "Video Starts per Session",
                changeKey: "plays_per_session_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "bounce_rate",
                label: "Bounce Rate",
                changeKey: "bounce_rate_percent_change",
                valueFormat: "percent",
                invertTrend: true
            },
            {
                key: "hours_watched",
                label: "Hours Watched",
                changeKey: "hours_watched_percent_change",
                valueFormat: "durationHours"
            },
            {
                key: "avg_hours_watched_per_playing_user",
                label: "Avg Hours per Playing User",
                changeKey: "avg_hours_watched_per_playing_user_percent_change",
                valueFormat: "durationHours"
            },
            {
                key: "avg_completion_rate",
                label: "Avg Completion Rate",
                changeKey: "avg_completion_rate_percent_change",
                valueFormat: "percent"
            }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap, schema);
    };

    private mapContentEngagementMetrics = (responseRow: Record<string, number | null | undefined>, schema?: Record<string, { description?: string }>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" | "durationHours"; invertTrend?: boolean }> = [
            {
                key: "users_using_recs_percent",
                label: "% Users Using Recs",
                changeKey: "users_using_recs_percent_change",
                valueFormat: "percent"
            },
            {
                key: "users_playing_top_20_brands_percent",
                label: replaceAnalyticsBrandTerminology("% Plays Top 20 Brands", this.props.customerConfig),
                changeKey: "users_playing_top_20_brands_percent_change",
                valueFormat: "percent"
            },
            {
                key: "avg_brands_played_per_user",
                label: replaceAnalyticsBrandTerminology("Brands per User", this.props.customerConfig),
                changeKey: "avg_brands_played_per_user_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "users_playing_only_one_brand_percent",
                label: replaceAnalyticsBrandTerminology("% Played 1 Brand", this.props.customerConfig),
                changeKey: "users_playing_only_one_brand_percent_change",
                valueFormat: "percent",
                invertTrend: true
            }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap, schema);
    };

    private formatTrendDate = (value: string): string => {
        try {
            const date = new Date(`${value}T00:00:00Z`);
            return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        } catch {
            return value;
        }
    };

    private formatSignedPercent = (value: number): string => {
        const rounded = Math.round(value);
        return `${rounded > 0 ? "+" : ""}${rounded}%`;
    };

    private renderSessionChangeLabel = (props: any): React.ReactElement | null => {
        const { x, y, width, height, value } = props;
        if (typeof x !== "number" || typeof y !== "number" || typeof width !== "number" || typeof height !== "number" || typeof value !== "number") {
            return null;
        }

        const theme = getWidgetTheme(this.state.isDarkTheme);
        const isPositive = value > 0;
        const isNegative = value < 0;
        const color = isPositive ? theme.positiveColor : isNegative ? theme.negativeColor : "#6c757d";
        const symbol = isPositive ? "↑" : isNegative ? "↓" : "—";
        const labelX = x + width + 8;
        const labelY = y + (height / 2);

        return (
            <g>
                <circle cx={labelX + 7} cy={labelY} r={7} fill={color} />
                <text x={labelX + 7} y={labelY + 3} fill="#000" fontSize={9} fontWeight={700} textAnchor="middle">
                    {symbol}
                </text>
                <text x={labelX + 19} y={labelY + 4} fill={color} fontSize={12} fontWeight={600}>
                    {this.formatSignedPercent(value)}
                </text>
            </g>
        );
    };

    private upsertSidebarEntry = (entries: DocsSidebarEntry[], entry: DocsSidebarEntry): DocsSidebarEntry[] => {
        const nextEntries = entries.filter(existing => existing.widgetKey !== entry.widgetKey);
        nextEntries.push(entry);
        return nextEntries;
    };

    private buildSidebarEntry = (widgetKey: KpiWidgetKey, response: unknown): DocsSidebarEntry | null => {
        const meta = (response as any)?.widget;
        if (!meta) {
            return null;
        }

        return {
            widgetKey,
            displayName: meta.display_name,
            description: meta.description,
            schema: (response as any)?.schema
        };
    };

    private markWidgetLoading = (): void => {
        this.setState({
            isLoading: true,
            error: null,
            widgetStates: createInitialWidgetStates(true),
            userKpiMetrics: [],
            trafficMetrics: [],
            userEngagementMetrics: [],
            contentEngagementMetrics: [],
            sessionTrendData: [],
            topPlatformsData: [],
            TopBrandsData: [],
            topGenresData: [],
            sidebarEntries: []
        });
    };

    private loadWidget = async (
        requestId: number,
        widgetKey: KpiWidgetKey,
        loadResponse: () => Promise<unknown>,
        applyResponse: (response: unknown) => Partial<KpiDashboardState>
    ): Promise<void> => {
        try {
            const response = await loadResponse();

            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            const sidebarEntry = this.buildSidebarEntry(widgetKey, response);

            this.setState(prevState => {
                const nextWidgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: null }
                };
                const appliedData = applyResponse(response) as Partial<KpiDashboardState>;

                return {
                    ...appliedData,
                    error: prevState.error,
                    sidebarEntries: sidebarEntry ? this.upsertSidebarEntry(prevState.sidebarEntries, sidebarEntry) : prevState.sidebarEntries,
                    widgetStates: nextWidgetStates,
                    isLoading: Object.values(nextWidgetStates).some(state => state.isLoading)
                } as KpiDashboardState;
            });
        } catch (error) {
            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            Logger.error(`Error fetching ${widgetKey}`, error);
            this.setState(prevState => {
                const nextWidgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: {
                        isLoading: false,
                        error: "Failed to load widget data"
                    }
                };

                return {
                    widgetStates: nextWidgetStates,
                    isLoading: Object.values(nextWidgetStates).some(state => state.isLoading)
                } as KpiDashboardState;
            });
        }
    };

    private fetchData = async (): Promise<void> => {
        const { appliedFilters } = this.state;
        const requestId = ++this.activeRequestId;

        this.markWidgetLoading();

        void this.loadWidget(requestId, "kpi_traffic", () => getAnalyticsWidget(
            this.props.customerConfig, "kpi_traffic", appliedFilters.startDate, appliedFilters.endDate,
            appliedFilters.attributionId, appliedFilters.brandName, appliedFilters.devicePlatform
        ), (response: unknown) => {
            const result = (response as any)?.result;
            const row = Array.isArray(result) ? result[0] : null;
            return { trafficMetrics: row && typeof row === "object" ? this.mapTrafficMetrics(row, (response as any)?.schema) : [] };
        });

        void this.loadWidget(requestId, "kpi_user_kpi", () => getAnalyticsWidget(
            this.props.customerConfig, "kpi_user_kpi", appliedFilters.startDate, appliedFilters.endDate,
            appliedFilters.attributionId, appliedFilters.brandName, appliedFilters.devicePlatform
        ), (response: unknown) => {
            const result = (response as any)?.result;
            const row = Array.isArray(result) ? result[0] : null;
            return { userKpiMetrics: row && typeof row === "object" ? this.mapUserKpiMetrics(row, (response as any)?.schema) : [] };
        });

        void this.loadWidget(requestId, "kpi_user_engagement", () => getAnalyticsWidget(
            this.props.customerConfig, "kpi_user_engagement", appliedFilters.startDate, appliedFilters.endDate,
            appliedFilters.attributionId, appliedFilters.brandName, appliedFilters.devicePlatform
        ), (response: unknown) => {
            const result = (response as any)?.result;
            const row = Array.isArray(result) ? result[0] : null;
            return { userEngagementMetrics: row && typeof row === "object" ? this.mapUserEngagementMetrics(row, (response as any)?.schema) : [] };
        });

        void this.loadWidget(requestId, "kpi_content_engagement", () => getAnalyticsWidget(
            this.props.customerConfig, "kpi_content_engagement", appliedFilters.startDate, appliedFilters.endDate,
            appliedFilters.attributionId, appliedFilters.brandName, appliedFilters.devicePlatform
        ), (response: unknown) => {
            const result = (response as any)?.result;
            const row = Array.isArray(result) ? result[0] : null;
            return { contentEngagementMetrics: row && typeof row === "object" ? this.mapContentEngagementMetrics(row, (response as any)?.schema) : [] };
        });

        void this.loadWidget(requestId, "kpi_traffic_trend", () => getAnalyticsWidget(
            this.props.customerConfig, "kpi_traffic_trend", appliedFilters.startDate, appliedFilters.endDate,
            appliedFilters.attributionId, appliedFilters.brandName, appliedFilters.devicePlatform
        ), (response: unknown) => {
            const trendDataRaw = (response as any)?.result;
            const sessionTrendData = (Array.isArray(trendDataRaw) ? trendDataRaw : [])
                .filter((row: Record<string, any>) => row?.date && typeof row?.session_count === "number")
                .map((row: Record<string, any>) => ({
                    date: row.date, sessionCount: row.session_count, deviceCount: row.device_count ?? 0,
                    playCount: row.play_count ?? 0, viewCount: row.view_count ?? 0, userCount: row.user_count ?? 0,
                    playingUserCount: row.playing_user_count ?? 0, viewingUserCount: row.viewing_user_count ?? 0
                }))
                .sort((a: SessionTrendPoint, b: SessionTrendPoint) => a.date.localeCompare(b.date));
            return { sessionTrendData };
        });

        void this.loadWidget(requestId, "kpi_top_platforms", () => getAnalyticsWidget(
            this.props.customerConfig,
            "kpi_top_platforms",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => {
            const topPlatformsRaw = (response as any)?.result;
            const topPlatformsData = (Array.isArray(topPlatformsRaw) ? topPlatformsRaw : [])
                .filter((row: Record<string, any>) => row?.device_platform)
                .map((row: Record<string, any>) => ({
                    devicePlatform: row.device_platform,
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopPlatformPoint, b: TopPlatformPoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            return { topPlatformsData };
        });

        void this.loadWidget(requestId, "kpi_top_things", () => getAnalyticsWidget(
            this.props.customerConfig,
            "kpi_top_things",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => {
            const topThingsRaw = (response as any)?.result;
            const TopBrandsData = (Array.isArray(topThingsRaw) ? topThingsRaw : [])
                .map((row: Record<string, any>) => ({
                    thingBrandName: row.thing_brandname || "Unknown",
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopChannelPoint, b: TopChannelPoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            return { TopBrandsData };
        });

        void this.loadWidget(requestId, "kpi_top_genres", () => getAnalyticsWidget(
            this.props.customerConfig,
            "kpi_top_genres",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => {
            const topGenresRaw = (response as any)?.result;
            const topGenresData = (Array.isArray(topGenresRaw) ? topGenresRaw : [])
                .map((row: Record<string, any>) => ({
                    genreName: row.genre_name || "Unknown",
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopGenrePoint, b: TopGenrePoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            return { topGenresData };
        });
    };

    private renderMetricCards = (
        metrics: KpiMetricCard[],
        theme: WidgetTheme,
        cardLayout?: { minCardWidth?: string; maxCardWidth?: string; gridGap?: string }
    ): ReactNode => {
        return (
            <MetricCards
                metrics={metrics as MetricCardItem[]}
                theme={theme}
                hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                emptyMessage="No KPI data available for the selected date range."
                minCardWidth={cardLayout?.minCardWidth}
                maxCardWidth={cardLayout?.maxCardWidth}
                gridGap={cardLayout?.gridGap}
            />
        );
    };

    private renderSessionTrendChart = (theme: WidgetTheme): ReactNode => {
        const { sessionTrendData, sidebarEntries } = this.state;
        const trendEntry = sidebarEntries.find(e => e.widgetKey === "kpi_traffic_trend");

        return (
            <WidgetCard
                title="Traffic Trends"
                description={trendEntry?.description}
                theme={theme}
                isEmpty={sessionTrendData.length === 0}
                emptyMessage="No session trend data available for the selected date range."
                style={{ marginTop: "24px" }}
            >
                <InteractiveLineChart
                    title=""
                    data={sessionTrendData}
                    xAxisDataKey="date"
                    metrics={[
                        { key: "sessionCount", name: "Sessions", color: "#007bff" },
                        { key: "deviceCount", name: "Devices", color: "#6f42c1" },
                        { key: "playCount", name: "Video Starts", color: "#fd7e14" },
                        { key: "viewCount", name: "Views", color: "#20c997" },
                        { key: "userCount", name: "Users", color: "#e83e8c" },
                        { key: "playingUserCount", name: "Playing Users", color: "#17a2b8" },
                        { key: "viewingUserCount", name: "Viewing Users", color: "#28a745" }
                    ]}
                    theme={theme}
                    height={320}
                    xAxisTickFormatter={this.formatTrendDate}
                    tooltipLabelFormatter={this.formatTrendDate}
                    tooltipValueFormatter={(value: number) => value.toLocaleString()}
                    yAxisLabel="Count"
                />
            </WidgetCard>
        );
    };

    private renderTopPlatformsSection = (theme: WidgetTheme): ReactNode => {
        const { topPlatformsData, sidebarEntries } = this.state;
        return (
            <WidgetCard
                title="Top Platforms"
                description={sidebarEntries.find(e => e.widgetKey === "kpi_top_platforms")?.description}
                theme={theme}
                isEmpty={topPlatformsData.length === 0}
                emptyMessage="No top platform data available for the selected date range."
                style={{ marginTop: "24px" }}
            >
                <p>Based on unique sessions with any event type</p>
                <SubChartBlock theme={theme}>
                    <h6>Session Count</h6>
                    <ResponsiveContainer width="100%" height={360}>
                        <BarChart
                            data={topPlatformsData}
                            layout="vertical"
                            margin={{ top: 5, right: 88, left: 0, bottom: 5 }}
                        >
                            <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                            <XAxis
                                type="number"
                                tick={{ fill: theme.text }}
                                axisLine={{ stroke: theme.border }}
                                tickLine={{ stroke: theme.border }}
                                tickFormatter={(value: number) => value.toLocaleString()}
                            />
                            <YAxis
                                type="category"
                                dataKey="devicePlatform"
                                width={160}
                                tick={{ fill: theme.text }}
                                axisLine={{ stroke: theme.border }}
                                tickLine={{ stroke: theme.border }}
                            />
                            <Tooltip
                                labelFormatter={(label: ReactNode) => String(label ?? "")}
                                formatter={(value: ValueType | undefined) => [(value ?? 0).toLocaleString(), "Sessions"]}
                                wrapperStyle={{ zIndex: KpiDashboard.CHART_TOOLTIP_Z_INDEX }}
                                contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                itemStyle={{ color: theme.text }}
                                labelStyle={{ color: theme.text }}
                            />
                            <Bar dataKey="sessionCount" fill="#007bff">
                                <LabelList dataKey="sessionPercentChange" content={this.renderSessionChangeLabel} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </SubChartBlock>
            </WidgetCard>
        );
    };

    private renderTopBrandsSection = (theme: WidgetTheme): ReactNode => {
        const { TopBrandsData, sidebarEntries } = this.state;
        return (
            <WidgetCard
                title={replaceAnalyticsBrandTerminology("Top Brands", this.props.customerConfig)}
                description={sidebarEntries.find(e => e.widgetKey === "kpi_top_things")?.description}
                theme={theme}
                isEmpty={TopBrandsData.length === 0}
                emptyMessage={replaceAnalyticsBrandTerminology("No top brands data available for the selected date range.", this.props.customerConfig)}
                style={{ marginTop: "24px" }}
            >
                <p>Based on unique sessions with a video_start or video_stop event</p>
                <SubChartBlock theme={theme}>
                    <h6>Session Count</h6>
                    <ResponsiveContainer width="100%" height={360}>
                        <BarChart
                            data={TopBrandsData}
                            layout="vertical"
                            margin={{ top: 5, right: 88, left: 0, bottom: 5 }}
                        >
                            <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                            <XAxis
                                type="number"
                                tick={{ fill: theme.text }}
                                axisLine={{ stroke: theme.border }}
                                tickLine={{ stroke: theme.border }}
                                tickFormatter={(value: number) => value.toLocaleString()}
                            />
                            <YAxis
                                type="category"
                                dataKey="thingBrandName"
                                width={KpiDashboard.TOP_BRAND_AXIS_WIDTH}
                                tickMargin={8}
                                tick={(tickProps: any) => this.renderTopBrandsAxisTick(tickProps, theme)}
                                axisLine={{ stroke: theme.border }}
                                tickLine={{ stroke: theme.border }}
                            />
                            <Tooltip
                                labelFormatter={(label: ReactNode) => String(label ?? "")}
                                formatter={(value: ValueType | undefined) => [(value ?? 0).toLocaleString(), "Sessions"]}
                                wrapperStyle={{ zIndex: KpiDashboard.CHART_TOOLTIP_Z_INDEX }}
                                contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                itemStyle={{ color: theme.text }}
                                labelStyle={{ color: theme.text }}
                            />
                            <Bar dataKey="sessionCount" fill="#007bff">
                                <LabelList dataKey="sessionPercentChange" content={this.renderSessionChangeLabel} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </SubChartBlock>
            </WidgetCard>
        );
    };

    private getTopBrandsAxisFont = (): string => {
        if (typeof document === "undefined") {
            return "14px sans-serif";
        }

        const bodyStyle = window.getComputedStyle(document.body);
        return bodyStyle.font || `${bodyStyle.fontSize || "14px"} ${bodyStyle.fontFamily || "sans-serif"}`;
    };

    private renderTopBrandsAxisTick = (tickProps: any, theme: WidgetTheme): ReactNode => {
        const { x, y, payload } = tickProps;
        const fullLabel = String(payload?.value ?? "");
        const axisFont = this.getTopBrandsAxisFont();
        const truncatedLabel = truncateAxisLabelToWidth(
            fullLabel,
            KpiDashboard.TOP_BRAND_AXIS_TEXT_MAX_WIDTH,
            axisFont
        );

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={0}
                    dominantBaseline="middle"
                    textAnchor="end"
                    fill={theme.text}
                    style={{ font: axisFont }}
                >
                    <title>{fullLabel}</title>
                    {truncatedLabel}
                </text>
            </g>
        );
    };

    private renderTopGenresSection = (theme: WidgetTheme): ReactNode => {
        const { topGenresData, sidebarEntries } = this.state;
        return (
            <WidgetCard
                title="Top Genres"
                description={sidebarEntries.find(e => e.widgetKey === "kpi_top_genres")?.description}
                theme={theme}
                isEmpty={topGenresData.length === 0}
                emptyMessage="No top genre data available for the selected date range."
                style={{ marginTop: "24px" }}
            >
                <p>Based on unique sessions with a video_start or video_stop event</p>
                <SubChartBlock theme={theme}>
                    <h6>Session Count</h6>
                    <ResponsiveContainer width="100%" height={360}>
                        <BarChart
                            data={topGenresData}
                            layout="vertical"
                            margin={{ top: 5, right: 88, left: 0, bottom: 5 }}
                        >
                            <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                            <XAxis
                                type="number"
                                tick={{ fill: theme.text }}
                                axisLine={{ stroke: theme.border }}
                                tickLine={{ stroke: theme.border }}
                                tickFormatter={(value: number) => value.toLocaleString()}
                            />
                            <YAxis
                                type="category"
                                dataKey="genreName"
                                width={160}
                                tick={{ fill: theme.text }}
                                axisLine={{ stroke: theme.border }}
                                tickLine={{ stroke: theme.border }}
                            />
                            <Tooltip
                                labelFormatter={(label: ReactNode) => String(label ?? "")}
                                formatter={(value: ValueType | undefined) => [(value ?? 0).toLocaleString(), "Sessions"]}
                                wrapperStyle={{ zIndex: KpiDashboard.CHART_TOOLTIP_Z_INDEX }}
                                contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                itemStyle={{ color: theme.text }}
                                labelStyle={{ color: theme.text }}
                            />
                            <Bar dataKey="sessionCount" fill="#007bff">
                                <LabelList
                                    dataKey="sessionPercentChange"
                                    content={this.renderSessionChangeLabel}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </SubChartBlock>
            </WidgetCard>
        );
    };

    public render(): ReactNode {
        const { isLoading, error, widgetStates, filters, filterOptions, appliedFilters, filtersCollapsed, isDarkTheme, userKpiMetrics, trafficMetrics, userEngagementMetrics, contentEngagementMetrics, sidebarOpen, sidebarEntries } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const alignedCardLayout = {
            minCardWidth: "200px",
            maxCardWidth: "200px",
            gridGap: "16px"
        };

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                <Container className="mw-100" key="kpi-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && (userKpiMetrics.length > 0 || trafficMetrics.length > 0 || userEngagementMetrics.length > 0 || contentEngagementMetrics.length > 0)}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={this.handleScheduleClick}
                        />
                        <Button
                            onClick={this.toggleTheme}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "24px",
                                padding: "4px 8px",
                                color: isDarkTheme ? "#ffffff" : "#000000"
                            }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                        <Button
                            onClick={() => this.setState(prevState => ({ filtersCollapsed: !prevState.filtersCollapsed }))}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                            title={filtersCollapsed ? "Show filters" : "Hide filters"}
                        >
                            {filtersCollapsed ? <Funnel /> : <FunnelFill />}
                        </Button>
                        <Button
                            onClick={this.toggleSidebar}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2 style={{ color: theme.text }}>KPI Dashboard</h2>
                        </Col>
                    </Row>

                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                        isCollapsed={filtersCollapsed}
                    />
                    <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                    {error && (
                        <Row>
                            <Col>
                                <Alert variant="danger">{error}</Alert>
                            </Col>
                        </Row>
                    )}

                    <TopMetricSectionsRow>
                        <TopMetricSection $start={1} $span={3}>
                            <WidgetCard
                                title="Traffic"
                                description={sidebarEntries.find(e => e.widgetKey === "kpi_traffic")?.description}
                                theme={theme}
                                isLoading={widgetStates.kpi_traffic.isLoading}
                                error={widgetStates.kpi_traffic.error}
                                isEmpty={!widgetStates.kpi_traffic.isLoading && !widgetStates.kpi_traffic.error && trafficMetrics.length === 0}
                                emptyMessage="No traffic data available for the selected date range."
                            >
                                {this.renderMetricCards(trafficMetrics, theme, alignedCardLayout)}
                            </WidgetCard>
                        </TopMetricSection>
                        <TopMetricSection $start={4} $span={5}>
                            <WidgetCard
                                title="User KPI"
                                description={sidebarEntries.find(e => e.widgetKey === "kpi_user_kpi")?.description}
                                theme={theme}
                                isLoading={widgetStates.kpi_user_kpi.isLoading}
                                error={widgetStates.kpi_user_kpi.error}
                                isEmpty={!widgetStates.kpi_user_kpi.isLoading && !widgetStates.kpi_user_kpi.error && userKpiMetrics.length === 0}
                                emptyMessage="No KPI data available for the selected date range."
                            >
                                {this.renderMetricCards(userKpiMetrics, theme, alignedCardLayout)}
                            </WidgetCard>
                        </TopMetricSection>
                    </TopMetricSectionsRow>
                    <Row className="mt-4 g-4">
                        <Col>
                            <WidgetCard
                                title="User Engagement"
                                description={sidebarEntries.find(e => e.widgetKey === "kpi_user_engagement")?.description}
                                theme={theme}
                                isLoading={widgetStates.kpi_user_engagement.isLoading}
                                error={widgetStates.kpi_user_engagement.error}
                                isEmpty={!widgetStates.kpi_user_engagement.isLoading && !widgetStates.kpi_user_engagement.error && userEngagementMetrics.length === 0}
                                emptyMessage="No user engagement data available for the selected date range."
                            >
                                {this.renderMetricCards(userEngagementMetrics, theme, alignedCardLayout)}
                            </WidgetCard>
                        </Col>
                    </Row>
                    <Row className="mt-4 g-4">
                        <Col>
                            <WidgetCard
                                title="Content Engagement"
                                description={sidebarEntries.find(e => e.widgetKey === "kpi_content_engagement")?.description}
                                theme={theme}
                                isLoading={widgetStates.kpi_content_engagement.isLoading}
                                error={widgetStates.kpi_content_engagement.error}
                                isEmpty={!widgetStates.kpi_content_engagement.isLoading && !widgetStates.kpi_content_engagement.error && contentEngagementMetrics.length === 0}
                                emptyMessage="No content engagement data available for the selected date range."
                            >
                                {this.renderMetricCards(contentEngagementMetrics, theme, alignedCardLayout)}
                            </WidgetCard>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {this.renderSessionTrendChart(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} lg={6} xl={4}>
                            {this.renderTopPlatformsSection(theme)}
                        </Col>
                        <Col md={12} lg={6} xl={4}>
                            {this.renderTopBrandsSection(theme)}
                        </Col>
                        <Col md={12} lg={6} xl={4}>
                            {this.renderTopGenresSection(theme)}
                        </Col>
                    </Row>

                    <ScheduleExportModal
                        show={this.state.showScheduleModal}
                        onHide={this.handleScheduleModalHide}
                        filters={this.state.appliedFilters}
                        pageKey="kpi_dashboard"
                        pageTitle="KPI Dashboard"
                        customerSlug={this.props.customerConfig.slug}
                        customerConfig={this.props.customerConfig}
                        editingSchedule={this.state.editingSchedule}
                        onSave={this.handleScheduleSave}
                    />
                </Container>
                </div>
                <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default KpiDashboard;
